import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { DeviceState, PointState } from "./generated/widget-fixture";
import { formatValue, valueAge } from "./format";
import type { Widget, WidgetInput, WidgetState } from "./types";

export function key(device: string, k: string) {
  return `${device}/${k}`;
}

/** The binding with a datapoint (or the first one), its point and its device. */
export function boundPoint(widget: Widget, input: WidgetInput, index = 0) {
  const bindings = (widget.bindings ?? []).filter((b) => b.datapoint);
  const b = bindings[index];
  if (!b?.datapoint) return undefined;
  return {
    binding: b,
    point: input.points?.[key(b.device, b.datapoint)],
    device: input.devices?.[b.device],
  };
}

export function useWidgetText() {
  const { t, i18n } = useTranslation("widget");
  const locale = i18n.language;
  return {
    t,
    locale,
    value: (v: number, p: Pick<PointState, "unit" | "decimals">, decimals = p.decimals) =>
      formatValue(v, p.unit, decimals, locale),
    age: (at: string, now: Date) => {
      const a = valueAge(new Date(at), now);
      if ("date" in a) return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(a.date);
      return "count" in a ? t(a.key, { count: a.count }) : t(a.key);
    },
    deviceState: (s: DeviceState["state"]) => t(`state.${s}`),
  };
}

/**
 * The common frame (DDB-35): a white card whose top line is a small icon and the label, then the body. It is a size
 * container, so a body can grow with the card instead of leaving a small value in an empty box.
 */
export function Frame({
  widget,
  state,
  title,
  icon,
  children,
  label,
}: {
  widget: Widget;
  state: WidgetState;
  title?: string;
  icon?: ReactNode;
  children: ReactNode;
  label?: string;
}) {
  return (
    <section
      data-widget={widget.type}
      data-widget-state={state}
      aria-label={label}
      className="@container flex h-full flex-col gap-1 rounded-xl border border-gray-200 bg-white p-4 text-sm shadow-xs"
    >
      {title && (
        <h3 className="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-700">
          {icon}
          <span className="truncate">{title}</span>
        </h3>
      )}
      {children}
    </section>
  );
}

/** The quiet last line of a card: state and age, pushed to the bottom. */
export function Footer({ children }: { children: ReactNode }) {
  return <div className="mt-auto flex flex-wrap items-baseline gap-x-1 pt-1">{children}</div>;
}

export function Note({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "warn" }) {
  return <p className={tone === "warn" ? "text-xs text-amber-900" : "text-xs text-gray-700"}>{children}</p>;
}

/** Notes on one line, split by a middle dot. */
export function Notes({ items }: { items: { text: string; tone?: "muted" | "warn" }[] }) {
  return (
    <Footer>
      {items.map((n, i) => (
        <span key={i} className="contents">
          {i > 0 && (
            <span aria-hidden="true" className="text-xs text-gray-500">
              ·
            </span>
          )}
          <span className={n.tone === "warn" ? "text-xs text-amber-900" : "text-xs text-gray-700"}>{n.text}</span>
        </span>
      ))}
    </Footer>
  );
}
